import { supabase } from './supabase';

export interface MemberNotification {
  id: string;
  memberId: string;
  message: string;
  type: string;
  createdAt: string;
  readAt: string | null;
  /** Set for workouts_added / workout_updated; null for workout_removed. Used to open workout detail. */
  workoutSessionId?: string | null;
  /** Scheduled date (YYYY-MM-DD) for the session; used for navigation to workout details. */
  scheduledDate?: string | null;
  workoutName?: string | null;
  workoutPlanName?: string | null;
  trainerName?: string | null;
}

/**
 * Unread count for the member (for "You have X notification(s)" banner).
 * Run database_member_notifications.sql in Supabase first.
 */
export async function getUnreadCount(memberId: string): Promise<number> {
  const { count, error } = await supabase
    .from('member_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('member_id', memberId)
    .is('read_at', null);
  if (error) {
    console.error('getUnreadCount error:', error);
    return 0;
  }
  return count ?? 0;
}

/**
 * List notifications for the member (newest first).
 */
export async function getNotifications(memberId: string): Promise<MemberNotification[]> {
  const { data, error } = await supabase
    .from('member_notifications')
    .select('id, member_id, message, type, created_at, read_at, workout_session_id, scheduled_date, workout_name, workout_plan_name, trainer_name')
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('getNotifications error:', error);
    return [];
  }
  return (data || []).map((row: any) => ({
    id: row.id,
    memberId: row.member_id,
    message: row.message,
    type: row.type,
    createdAt: row.created_at,
    readAt: row.read_at,
    workoutSessionId: row.workout_session_id ?? undefined,
    scheduledDate: row.scheduled_date ?? undefined,
    workoutName: row.workout_name ?? undefined,
    workoutPlanName: row.workout_plan_name ?? undefined,
    trainerName: row.trainer_name ?? undefined,
  }));
}

/**
 * Mark one notification as read.
 */
export async function markAsRead(memberId: string, notificationId: string): Promise<void> {
  await supabase
    .from('member_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', notificationId)
    .eq('member_id', memberId);
}

/**
 * Mark all notifications as read for the member.
 */
export async function markAllAsRead(memberId: string): Promise<void> {
  await supabase
    .from('member_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('member_id', memberId)
    .is('read_at', null);
}

/** Payload for INSERT on member_notifications (realtime). */
export type MemberNotificationInsertDetail = {
  type?: string | null;
};

/**
 * Subscribe to new notifications for this member (e.g. when trainer deletes a workout and trigger inserts a row).
 * `onNewNotification` receives `{ type }` from the new row (e.g. workout_removed) for dashboard refetch fallback.
 * Enable Realtime first: add member_notifications to supabase_realtime publication (see database_member_notifications_realtime.sql).
 */
export function subscribeToNewNotifications(
  memberId: string,
  onNewNotification: (detail?: MemberNotificationInsertDetail) => void
): { unsubscribe: () => void } {
  const channel = supabase
    .channel(`member_notifications:${memberId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'member_notifications',
        filter: `member_id=eq.${memberId}`,
      },
      (payload: { new?: { type?: string } }) => {
        onNewNotification({ type: payload?.new?.type ?? null });
      }
    )
    .subscribe();
  return {
    unsubscribe: () => {
      if (channel?.unsubscribe) channel.unsubscribe();
    },
  };
}
